import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStoredRFPs, getStoredProjects } from './storageService';

const OrdersDashboard = () => {
  const [activeTab, setActiveTab] = useState('projects'); // 'projects' | 'rfps'
  const [rfps, setRfps] = useState([]);
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setRfps(getStoredRFPs());
    setProjects(getStoredProjects());
  }, []);

  const filteredProjects = projects.filter(p =>
    p.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.title && p.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.client?.name && p.client.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.client?.organization && p.client.organization.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredRfps = rfps.filter(r => 
    r.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.companyName && r.companyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (r.contactName && r.contactName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (r.projectType && r.projectType.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const exportAllData = () => {
    const data = { projects, rfps, exportedAt: new Date().toISOString() };
    const jsonStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(data, null, 2));
    const dlAnchor = document.createElement('a');
    dlAnchor.setAttribute("href", jsonStr);
    dlAnchor.setAttribute("download", `gurus_workshop_pipeline_${Date.now()}.json`);
    document.body.appendChild(dlAnchor);
    dlAnchor.click();
    dlAnchor.remove();
  };

  return (
    <div style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', minHeight: '100vh', paddingTop: '3rem', paddingBottom: '6rem' }}>
      <div className="container-fluid-cockpit">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '2.5rem' }}>
          <div>
            <div className="pill-badge pill-blue" style={{ marginBottom: '0.6rem' }}>
              INTERNAL OPERATIONS
            </div>
            <h1 style={{ fontSize: '2.4rem', margin: 0, fontWeight: 900 }}>
              Lead &amp; Project Pipeline Portal
            </h1>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
              Inspect and manage enterprise software proposals, technical RFPs, and active milestone deliverables.
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
            <button onClick={exportAllData} className="btn-outline-blue" style={{ padding: '0.65rem 1.4rem', fontSize: '0.9rem' }}>
              ⬇ Export Pipeline (JSON)
            </button>
            <Link to="/rfp" className="btn-primary-blue" style={{ padding: '0.65rem 1.4rem', fontSize: '0.9rem' }}>
              + Create New RFP
            </Link>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="row" style={{ marginBottom: '2.5rem' }}>
          <div className="col-lg-4 col-md-6 mb-3">
            <div className="workshop-card card-blue" style={{ padding: '1.6rem', backgroundColor: '#ffffff' }}>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                Active Client Projects
              </div>
              <div style={{ fontSize: '2.2rem', fontWeight: 900, color: 'var(--accent-blue)', marginTop: '0.2rem', fontFamily: 'Outfit' }}>
                {projects.length}
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                {projects.filter(p => p.status === 'Signed & In Production').length} in active engineering production
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-6 mb-3">
            <div className="workshop-card card-emerald" style={{ padding: '1.6rem', backgroundColor: '#ffffff' }}>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                Enterprise RFPs Received
              </div>
              <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#059669', marginTop: '0.2rem', fontFamily: 'Outfit' }}>
                {rfps.length}
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                {rfps.filter(r => r.status === 'New').length} pending initial technical scoping
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-6 mb-3">
            <div className="workshop-card" style={{ padding: '1.6rem', backgroundColor: '#ffffff' }}>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                Total Tracked Pipeline
              </div>
              <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#0f172a', marginTop: '0.2rem', fontFamily: 'Outfit' }}>
                {projects.length + rfps.length}
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                Combined active engagements &amp; proposals
              </div>
            </div>
          </div>
        </div>

        {/* Tab & Search Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.8rem' }}>
          <div style={{ display: 'flex', gap: '0.6rem' }}>
            <button
              onClick={() => setActiveTab('projects')}
              className={`tab-btn ${activeTab === 'projects' ? 'active' : ''}`}
            >
              Client Projects &amp; Proposals ({projects.length})
            </button>
            <button
              onClick={() => setActiveTab('rfps')}
              className={`tab-btn ${activeTab === 'rfps' ? 'active' : ''}`}
            >
              Technical RFPs ({rfps.length})
            </button>
          </div>

          <div style={{ minWidth: '280px' }}>
            <input
              type="text"
              className="workshop-input"
              placeholder="Search by ID, title, organization, or client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: '0.55rem 1rem', fontSize: '0.9rem' }}
            />
          </div>
        </div>

        {/* Tab content */}
        {activeTab === 'projects' ? (
          <div>
            {filteredProjects.length === 0 ? (
              <div className="workshop-card" style={{ textAlign: 'center', padding: '4rem 1rem', color: 'var(--text-secondary)', backgroundColor: '#ffffff' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.8rem' }}>💼</div>
                <h4 style={{ color: '#0f172a' }}>No Client Projects Found</h4>
                <p>Submit an inquiry through the <Link to="/#intake" style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>Project Intake Configurator</Link>.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                {filteredProjects.map((proj) => (
                  <div key={proj.id} className="workshop-card card-blue" style={{ padding: '1.8rem', backgroundColor: '#ffffff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.8rem', marginBottom: '1rem' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.15rem', color: 'var(--accent-blue)' }}>
                            {proj.id}
                          </span>
                          <span className={`pill-badge ${proj.status === 'Signed & In Production' ? 'pill-emerald' : 'pill-blue'}`} style={{ fontSize: '0.72rem', padding: '0.15rem 0.55rem' }}>
                            {proj.status}
                          </span>
                          <span className="pill-badge" style={{ backgroundColor: '#f1f5f9', color: '#475569', fontSize: '0.72rem', padding: '0.15rem 0.55rem' }}>
                            {proj.category}
                          </span>
                        </div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', marginTop: '0.4rem' }}>
                          {proj.title}
                        </div>
                        <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                          Client: <strong>{proj.client?.name}</strong> {proj.client?.organization ? `(${proj.client.organization})` : ''} &bull; {proj.client?.email}
                        </div>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <Link 
                          to={`/project/${proj.id}`} 
                          className="btn-primary-blue"
                          style={{ padding: '0.5rem 1.2rem', fontSize: '0.85rem' }}
                        >
                          View Tracker →
                        </Link>
                      </div>
                    </div>

                    <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '1rem', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.92rem', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}>
                      <strong style={{ color: 'var(--text-primary)' }}>Scope Overview:</strong> {proj.description || 'No detailed scope provided.'}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.8rem', fontSize: '0.84rem', color: 'var(--text-muted)' }}>
                      <div>
                        Platforms: <strong>{(proj.platforms || []).join(', ') || 'Web & Cloud'}</strong>
                      </div>
                      <div>
                        Created: {new Date(proj.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            {filteredRfps.length === 0 ? (
              <div className="workshop-card" style={{ textAlign: 'center', padding: '4rem 1rem', color: 'var(--text-secondary)', backgroundColor: '#ffffff' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.8rem' }}>📋</div>
                <h4 style={{ color: '#0f172a' }}>No RFPs Recorded</h4>
                <p>Submit an RFP using the <Link to="/rfp" style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>RFP Portal</Link>.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                {filteredRfps.map((rfp) => (
                  <div key={rfp.id} className="workshop-card card-blue" style={{ padding: '1.8rem', backgroundColor: '#ffffff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.8rem', marginBottom: '1rem' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.15rem', color: 'var(--accent-blue)' }}>
                            {rfp.id}
                          </span>
                          <span className="pill-badge pill-blue" style={{ fontSize: '0.72rem', padding: '0.15rem 0.55rem' }}>
                            {rfp.status}
                          </span>
                          {rfp.ndaRequired && (
                            <span className="pill-badge pill-amber" style={{ fontSize: '0.72rem', padding: '0.15rem 0.55rem' }}>
                              NDA Required
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', marginTop: '0.3rem' }}>
                          {rfp.companyName || 'Independent Client'} &bull; <span style={{ fontWeight: 500, fontSize: '0.95rem', color: 'var(--text-secondary)' }}>{rfp.contactName}</span>
                        </div>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '1.15rem' }}>{rfp.budgetTier}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Target: {rfp.timeline}</div>
                      </div>
                    </div>

                    <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '1rem', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.92rem', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}>
                      <strong style={{ color: 'var(--text-primary)' }}>Scope:</strong> {rfp.projectScope}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.8rem', fontSize: '0.86rem', color: 'var(--text-secondary)' }}>
                      <div>
                        <strong>Stack:</strong> {(rfp.techStack || []).join(', ')}
                      </div>
                      <div>
                        ✉️ <a href={`mailto:${rfp.email}`} style={{ color: 'var(--accent-blue)', textDecoration: 'none', fontWeight: 600 }}>{rfp.email}</a>
                        {rfp.phone && <span style={{ marginLeft: '1rem' }}>📞 {rfp.phone}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersDashboard;
